import { useState } from "react"
import { Auth } from "aws-amplify"
import { useSnackbar } from "notistack"
import i18n from "i18n"
import { useAuthContext } from "context/auth/authContext"
import {
  fetchInvoicePdfBlob,
  fetchInvopopPdfBlob,
  downloadBlob,
} from "../Dashboard/fetchInvoicePdf"

const INVOPOP_READY_STATUSES = [
  "sent",
  "accepted",
  "accepted_with_incidents",
  "void",
  "cancelled",
]

const INVOPOP_PENDING_STATUSES = ["pending", "processing"]

const INVOPOP_FAILED_STATUSES = ["rejected", "error", "failing"]

const usePdfDownload = (invoiceData = {}) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useAuthContext()
  const organizationId = user?.["custom:organizationId"]

  const downloadPdf = async onBeforeDownload => {
    const { invoiceId, invoiceNumber, issueDate, issued, invopopData } =
      invoiceData

    if (!invoiceId || !organizationId) return

    // Una factura emitida en `not_sent` *con* siloId se envió a Verifactu y el envío falló. El
    // renderer nativo solo marca "BORRADOR" en borradores, así que caer ahí produciría un PDF
    // numerado y de aspecto válido sin QR tributario. Se bloquea en lugar de emitirlo.
    //
    // Sin siloId no hubo envío: o la organización no tiene Invopop, o la factura es anterior a
    // la integración. El backend solo escribe siloId al enviar, y `shows_tax_qr` no pinta QR a
    // una organización sin Invopop, así que para estas facturas el PDF nativo es el documento
    // correcto y bloquearlo las deja sin ninguno.
    if (
      issued &&
      invopopData?.invopopStatus === "not_sent" &&
      invopopData?.siloId
    ) {
      enqueueSnackbar(i18n.t("invoices.pdfNotAvailableNotSent"), {
        variant: "warning",
      })
      if (onBeforeDownload && typeof onBeforeDownload === "function")
        onBeforeDownload()
      return
    }

    setIsDownloading(true)
    enqueueSnackbar(i18n.t("invoices.downloadingPdf"), { variant: "info" })

    if (onBeforeDownload && typeof onBeforeDownload === "function")
      onBeforeDownload()

    try {
      const token = (await Auth.currentSession()).getIdToken().getJwtToken()
      const siloId = invopopData?.siloId
      const invopopStatus = invopopData?.invopopStatus
      const useInvopop =
        INVOPOP_READY_STATUSES.includes(invopopStatus) && !!siloId

      if (siloId && INVOPOP_PENDING_STATUSES.includes(invopopStatus)) {
        enqueueSnackbar(i18n.t("invoices.pdfNotReadyYet"), {
          variant: "warning",
        })
        return
      }

      if (siloId && INVOPOP_FAILED_STATUSES.includes(invopopStatus)) {
        enqueueSnackbar(i18n.t("invoices.pdfNotAvailableRejected"), {
          variant: "warning",
        })
        return
      }
      const blob = useInvopop
        ? await fetchInvopopPdfBlob({ siloId, token })
        : await fetchInvoicePdfBlob({ invoiceId, organizationId, token })

      if (!blob) {
        console.error("No PDF blob could be created from response")
        enqueueSnackbar(i18n.t("invoices.downloadPdfError"), {
          variant: "error",
        })
        return
      }

      const dateStr = issueDate ? issueDate.split("T")[0] : "no_date"
      const filename =
        issued && invoiceNumber
          ? `invoice_${invoiceNumber}.pdf`
          : `invoice_${dateStr || invoiceId}.pdf`

      downloadBlob(blob, filename)
    } catch (error) {
      console.error("Error downloading PDF:", error)
      enqueueSnackbar(i18n.t("invoices.downloadPdfError"), { variant: "error" })
    } finally {
      setIsDownloading(false)
    }
  }

  return { isDownloading, downloadPdf }
}

export default usePdfDownload
